import { useState } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LinearIcon } from "@/components/icons";
import { toast } from "sonner";
import { Check, CheckCircle2, Copy } from "lucide-react";

interface LinearConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

export function LinearConnectDialog({
  open,
  onOpenChange,
  onConnected,
}: LinearConnectDialogProps) {
  const [apiKey, setApiKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [connectResult, setConnectResult] = useState<{
    webhookUrl: string;
    webhookSecret: string;
  } | null>(null);

  const connectMutation = trpc.integration.linear.connectApiKey.useMutation({
    onSuccess: (data) => {
      toast.success("Linear API key connected successfully");
      setConnectResult({
        webhookUrl: data.webhookUrl,
        webhookSecret: data.webhookSecret,
      });
      onConnected();
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to connect Linear API key");
    },
  });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleConnectSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      toast.error("Please enter a Linear API key");
      return;
    }
    connectMutation.mutate({
      apiKey: apiKey.trim(),
      displayName: displayName.trim() || undefined,
      webhookSecret: webhookSecret.trim() || undefined,
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    setConnectResult(null);
    setApiKey("");
    setDisplayName("");
    setWebhookSecret("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinearIcon className="h-5 w-5" />
            Connect Linear API Key
          </DialogTitle>
          <DialogDescription>
            Create a Personal API Key in Linear under{" "}
            <code className="font-mono text-xs">
              Settings &gt; Account &gt; API
            </code>
            .
          </DialogDescription>
        </DialogHeader>

        {connectResult ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-800 dark:text-emerald-300">
              <p className="font-medium flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                Connection verified!
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Optionally configure a webhook in Linear to receive
                bi-directional state updates.
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  label: "Webhook URL",
                  value: connectResult.webhookUrl,
                  field: "webhookUrl",
                },
                {
                  label: "Webhook Signing Secret",
                  value: connectResult.webhookSecret,
                  field: "webhookSecret",
                },
              ].map((item) => (
                <div key={item.field} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {item.label}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={item.value}
                      className="font-mono text-xs"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => handleCopy(item.value, item.field)}
                    >
                      {copiedField === item.field ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleConnectSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="linear-api-key">Linear API Key</Label>
              <Input
                id="linear-api-key"
                type="password"
                placeholder="lin_api_..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="linear-display-name">
                Display Name (optional)
              </Label>
              <Input
                id="linear-display-name"
                placeholder="e.g. My Workspace Linear"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="linear-webhook-secret">
                Custom Webhook Secret (optional)
              </Label>
              <Input
                id="linear-webhook-secret"
                placeholder="Leave blank to auto-generate"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={connectMutation.isPending || !apiKey.trim()}
              >
                {connectMutation.isPending ? "Connecting..." : "Connect"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
